import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import styled from 'styled-components';

import DateRangeFilter from '@/components/DateRangeFilter';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading, SectionHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
} from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { SortState, SponsornameResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import SponsornameTableRow from './components/SponsornameTableRow';

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 128px;
      min-width: 128px;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
      font-weight: 700;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
`;

const Filters = styled.div`
  display: flex;
`;

const Sponsorname = () => {
  const now = DateTime.now();

  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({
    rangeStart: now.minus({ days: 1 }),
  });

  const [search, setSearch] = useState('');

  const [sort, setSort] = useState<SortState>({
    direction: SortDirection.ASC,
    property: 'sponsor',
  });

  const debouncedSearch = useDebounce(search, 400);

  const {
    isLoading,
    isError,
    data: sponsors,
  } = useQuery<SponsornameResource[]>(
    ['sponsornames', { search: debouncedSearch, sort, selectedDateRange }],
    () =>
      api.get<never, SponsornameResource[]>('/v1/sponsornames', {
        params: {
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
          ...(selectedDateRange.rangeStart &&
            selectedDateRange.rangeEnd && {
              'filter[dateFrom]':
                selectedDateRange.rangeStart.toFormat('y-MM-d'),
              'filter[dateTo]': selectedDateRange.rangeEnd
                .plus({ days: 1 })
                .toFormat('y-MM-d'),
            }),
          ...(selectedDateRange.rangeStart &&
            !selectedDateRange.rangeEnd && {
              'filter[exactDate]':
                selectedDateRange.rangeStart.toFormat('y-MM-d'),
            }),
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
        },
      }),
    {
      refetchOnMount: false,
      staleTime: 120 * 1000,
    },
  );

  const baseValues = sponsors?.filter((sponsor) =>
    sponsor.sponsor.includes('_0'),
  );

  const renderDateAsText = () => {
    if (!selectedDateRange.rangeStart && !selectedDateRange.rangeEnd) {
      return 'All time';
    }
    if (!!selectedDateRange.rangeStart && !!selectedDateRange.rangeEnd) {
      return `${selectedDateRange.rangeStart.toFormat('d/MM/y')} - ${selectedDateRange.rangeEnd.toFormat('d/MM/y')}`;
    }
    return selectedDateRange.rangeStart?.toFormat('d/MM/y') ?? '';
  };

  return (
    <>
      <PageHeading>Sponsorname Data</PageHeading>
      <SearchBar value={search} setValue={setSearch} />
      <TableHeader>
        <StyledSectionHeading>
          Data for: {renderDateAsText()}
        </StyledSectionHeading>
        <Filters>
          <DateRangeFilter
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
          />
          <FullScreenButton />
        </Filters>
      </TableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="sponsor"
                title="Sponsor"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_avg_scroll"
                title="Average Scroll"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_avg_time"
                title="Average Time"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="avg_time_per_view"
                title="Time / Views"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_scroll_starts"
                title="Average Scroll Starts"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_total_time"
                title="Total Time"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_uniques"
                title="Uniques"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_views"
                title="Views"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_views_loyal"
                title="Views Loyal"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_views_quality"
                title="Views Quality"
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!sponsors?.length}
            >
              {sponsors?.map((sponsor, index) => (
                <SponsornameTableRow
                  baseValues={baseValues}
                  sponsor={sponsor}
                  key={index}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </>
  );
};

export default Sponsorname;
